<script setup lang="ts">
import { computed } from 'vue';
import { computedAsync } from '@vueuse/core';

import { usePageConnectorStore } from '@apps/main/new-desktop/core/stores/pageConnectorStore';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';

import { TransportType } from '@libs/trip-planner-interfaces';
import { isExperimentAsync } from '@libs/feature-toggle';

import type { UnavailableItineraryFlightSegment } from '../../../models';
import { useTripOverviewPageStore } from '../../../stores';
import ItineraryTimeline from '../ItineraryTimeline.vue';
import ItineraryTransportSelector from '../ItineraryTransportSelector.vue';

import UnavailableFlightProduct from './UnavailableFlightProduct.vue';
import FlightSegmentHeader from './FlightSegmentHeader.vue';
import ExperimentalFlightSegmentHeader from './ExperimentalFlightSegmentHeader.vue';

interface UnavailableFlightSegmentProps {
  segment: UnavailableItineraryFlightSegment;
}

const props = defineProps<UnavailableFlightSegmentProps>();

interface UnavailableFlightSegmentEmits {
  (eventName: 'remove-filter', id: string): void;
  (eventName: 'show-more-filters'): void;
}

const emit = defineEmits<UnavailableFlightSegmentEmits>();

const pageConnectorStore = usePageConnectorStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const recipeStore = useRecipeStore();
const browserStore = useBrowserStore();
const statsigEventsStore = useStatsigEventsStore();

const isFirstTransportSegment = computed<boolean>(
  () =>
    recipeStore.flightOnlyRecipe[0]?.ingredientId === props.segment.ingredientId
);

const isMobileHeaderExperiment = computedAsync<boolean>(
  () => isExperimentAsync('mobile_flight_header'),
  false
);

const shouldShowExperiment = computed(
  () => isMobileHeaderExperiment.value && browserStore.isSmallerThan600px
);

function removeFlight(): void {
  tripOverviewPageStore.selectTransport(props.segment, TransportType.None);
}
</script>

<template>
  <ItineraryTimeline
    :segment-type="props.segment.segmentType"
    :is-experimental-header-enabled="shouldShowExperiment"
    :class="{
      'has-experimental-header': shouldShowExperiment,
      'hide-timeline':
        shouldShowExperiment &&
        props.segment.suggestionType === undefined &&
        isFirstTransportSegment,
    }"
    class="unavailable-flight-segment"
  >
    <template v-if="shouldShowExperiment" #experimental-header>
      <ExperimentalFlightSegmentHeader
        :segment="props.segment"
        :show-trash-bin="true"
        @remove-flight="removeFlight"
        @show-more-filters="emit('show-more-filters')"
        @add-filter="
          statsigEventsStore.logTripOverviewFilterButtonClicked();
          emit('show-more-filters');
        "
      />
    </template>
    <template v-if="!shouldShowExperiment" #dropdown>
      <ItineraryTransportSelector
        :transport-type="TransportType.Flight"
        :disabled="false"
        @select-transport="
          tripOverviewPageStore.selectTransport(segment, $event)
        "
      />
    </template>
    <template v-if="!shouldShowExperiment" #header>
      <FlightSegmentHeader
        :segment="props.segment"
        :print-mode="false"
        @show-more-filters="emit('show-more-filters')"
        @remove-filter="emit('remove-filter', $event)"
      />
    </template>

    <template #content>
      <UnavailableFlightProduct
        :reasons="props.segment.reasons"
        :message="
          tripOverviewPageStore.getUnavailabilityMessage(
            props.segment.segmentType,
            props.segment.reasons
          )
        "
        @edit-filters="pageConnectorStore.openFlightListFilters()"
        @try-another-date="tripOverviewPageStore.scrollToCalendar()"
      />
    </template>
  </ItineraryTimeline>
</template>
